import { FormDrawer, FormDrawerContent, Id, LaravelResponse, SelectedRows, isSuccess, useToasts } from "@dgs/core";
import { AxiosResponse } from "axios";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { mapToBulkRequest } from "../../api/mappings";
import { EditEventUserResponsibilityFormValues, EditEventUserResponsibilityRequest } from "../../types/user";
import { UserResponsibilityByEventSelect } from "./UserResponsibilitySelect";

const emptyInitialValues: EditEventUserResponsibilityFormValues = {
	responsibilities: [],
};

interface Props {
	open: boolean;
	selectedRows: SelectedRows;
	handleClose: () => void;
	onClosed?: () => void;
	reloadList: () => void;
	editUserResponsibility: (values: EditEventUserResponsibilityRequest) => Promise<AxiosResponse<LaravelResponse<void>>>;
	initialValues?: EditEventUserResponsibilityFormValues;
	eventId: Id;
}

export const EditEventUserResponsibilityDrawer: FC<Props> = ({
	open,
	handleClose,
	selectedRows,
	reloadList,
	onClosed,
	editUserResponsibility,
	initialValues = emptyInitialValues,
	eventId,
}) => {
	const { t } = useTranslation();
	const { showToast } = useToasts();

	const handleSubmit = useCallback(
		async (values: EditEventUserResponsibilityFormValues) => {
			const response = await editUserResponsibility({
				...mapToBulkRequest(selectedRows),
				userResponsibilityIds: values.responsibilities.map(({ id }) => id),
			});

			if (isSuccess(response)) {
				showToast({
					type: "success",
					body: t("User responsibilities were successfully edited."),
					title: t("Edited user responsibilities"),
				});
				reloadList();

				return true;
			}

			return false;
		},
		[editUserResponsibility, reloadList, selectedRows, showToast, t],
	);

	const validate = useCallback(
		(values: EditEventUserResponsibilityFormValues) => {
			const errors: { [K in keyof EditEventUserResponsibilityFormValues]?: any } = {};
			if (values.responsibilities.length === 0) {
				errors.responsibilities = t("Required field");
			}
			return errors;
		},
		[t],
	);

	return (
		<FormDrawer
			open={open}
			validate={validate}
			onSubmit={handleSubmit}
			initialValues={initialValues}
			onClose={handleClose}
			onClosed={onClosed}
			title={t("Edit user responsibilities")}
			renderForm={(props) => (
				<FormDrawerContent {...props}>
					<UserResponsibilityByEventSelect
						eventId={eventId}
						label={t("Responsibilities")}
						name="responsibilities"
						required
					/>
				</FormDrawerContent>
			)}
		/>
	);
};
